import { extractErrorMessage } from "../utils/acceptSuggestions";
import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Shield, Eye, EyeOff, CheckCircle2, X, Lock } from "lucide-react";

const PASSWORD_RULES = [
  { test: (p) => p.length >= 8, label: "At least 8 characters" },
  { test: (p) => /[A-Z]/.test(p), label: "One uppercase letter" },
  { test: (p) => /[a-z]/.test(p), label: "One lowercase letter" },
  { test: (p) => /[0-9]/.test(p), label: "One number" },
  { test: (p) => /[!@#$%^&*()_+\-=[\]{};':"|,.<>?/]/.test(p), label: "One special character" },
];

export default function ForcePasswordResetPage() {
  const { api, updateUser } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const passedRules = PASSWORD_RULES.filter((r) => r.test(newPassword));
  const allPassed = passedRules.length === PASSWORD_RULES.length;
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allPassed && passwordsMatch && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      await api.post("/auth/force-reset-password", { new_password: newPassword });
      toast.success("Password updated successfully!");
      updateUser({ must_reset_password: false });
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to reset password"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 flex items-center justify-center p-4" data-testid="force-reset-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-sky-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-sky-500/30">
            <Shield className="w-8 h-8 text-sky-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Set Your Password</h1>
          <p className="text-slate-400 text-sm">
            Your account was created with a temporary password.<br />
            Please set a secure password to continue.
          </p>
        </div>

        <Card className="border-slate-700 bg-slate-800/80 backdrop-blur-sm shadow-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-sky-400" />
              New Password
            </CardTitle>
            <CardDescription className="text-slate-400">
              Choose a strong password that meets all requirements below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-slate-300">New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="bg-slate-700/50 border-slate-600 text-white pr-10 focus:border-sky-500"
                    autoFocus
                    data-testid="force-reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Confirm Password</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="bg-slate-700/50 border-slate-600 text-white focus:border-sky-500"
                  data-testid="force-reset-confirm-password"
                />
                {confirmPassword && !passwordsMatch && (
                  <p className="text-xs text-red-400 flex items-center gap-1">
                    <X className="w-3 h-3" /> Passwords do not match
                  </p>
                )}
                {passwordsMatch && (
                  <p className="text-xs text-emerald-400 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Passwords match
                  </p>
                )}
              </div>

              {/* Password strength rules */}
              <div className="p-3 bg-slate-700/30 rounded-lg space-y-1.5">
                <p className="text-xs font-semibold text-slate-400 mb-2">Password Requirements</p>
                {PASSWORD_RULES.map((rule, i) => {
                  const passed = rule.test(newPassword);
                  return (
                    <div key={i} className={`flex items-center gap-2 text-xs ${passed ? "text-emerald-400" : "text-slate-500"}`}>
                      {passed ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> : <div className="w-3.5 h-3.5 rounded-full border border-slate-500 flex-shrink-0" />}
                      {rule.label}
                    </div>
                  );
                })}
              </div>

              {/* Strength bar */}
              <div className="space-y-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className={`h-1.5 flex-1 rounded-full transition-all ${
                        passedRules.length >= i
                          ? passedRules.length <= 2 ? "bg-red-500" : passedRules.length <= 4 ? "bg-amber-500" : "bg-emerald-500"
                          : "bg-slate-600"
                      }`}
                    />
                  ))}
                </div>
                <p className="text-[0.65rem] text-right text-slate-500">
                  {passedRules.length === 0 ? "" : passedRules.length <= 2 ? "Weak" : passedRules.length <= 4 ? "Good" : "Strong"}
                </p>
              </div>

              <Button
                type="submit"
                disabled={!canSubmit}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold"
                data-testid="force-reset-submit"
              >
                {submitting ? "Updating..." : "Set Password & Continue"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-xs mt-4">
          This is a one-time setup. You won't be asked again.
        </p>
      </div>
    </div>
  );
}
